
import ExcelJS from "exceljs";
import { DOCUMENT_API_V1 } from "./apiBase";

const ENDPOINT =
  `${DOCUMENT_API_V1}/inventory/export/excel`;

// ------------------------------------------
// Build inventory export URL
// ------------------------------------------

function buildUrl(filters = {}) {
  const params = new URLSearchParams();

  const values = {
    document_id: filters.documentId,
    project_id: filters.projectId,
    search: filters.search,
    condition: filters.condition,
  };

  for (const [key, value] of Object.entries(values)) {
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      params.set(key, String(value));
    }
  }

  const query = params.toString();

  return query
    ? `${ENDPOINT}?${query}`
    : ENDPOINT;
}

// ------------------------------------------
// Fetch Excel workbook
// ------------------------------------------


async function getExcelBlob(filters = {}) {
  const url = buildUrl(filters);

  let response;

  try {
    response = await fetch(url, {
      method: "GET",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

        // Bypass ngrok free-plan browser warning
        "ngrok-skip-browser-warning": "true",
      },
    });
  } catch (error) {
    throw new Error(
      `Cannot connect to Excel API: ${error.message}`
    );
  }

  // Detect ngrok warning or tunnel errors
  const ngrokError = response.headers.get(
    "ngrok-error-code"
  );

  if (ngrokError) {
    throw new Error(
      `Ngrok error (${ngrokError}). The Excel request did not reach the backend.`
    );
  }

  if (!response.ok) {
    const message = await response
      .text()
      .catch(() => "");

    throw new Error(
      `Excel API failed (${response.status}): ` +
      message.slice(0, 300)
    );
  }

  const contentType = (
    response.headers.get("content-type") || ""
  ).toLowerCase();

  // Reject unexpected HTML, JSON, or text responses
  if (
    contentType.includes("text/html") ||
    contentType.includes("text/plain") ||
    contentType.includes("application/json")
  ) {
    const body = await response.text();

    throw new Error(
      `Expected Excel but received ${contentType}: ` +
      body.slice(0, 250)
    );
  }

  const blob = await response.blob();

  if (!blob.size) {
    throw new Error(
      "The Excel API returned an empty file."
    );
  }

  return {
    blob,
    contentDisposition:
      response.headers.get("content-disposition") || "",
  };
}


// ------------------------------------------
// Parse Excel into table rows
// ------------------------------------------

export async function getInventoryExcelRows(
  filters = {}
) {
  const { blob } = await getExcelBlob(filters);

  const workbook = new ExcelJS.Workbook();

  try {
    await workbook.xlsx.load(
      await blob.arrayBuffer()
    );
  } catch (error) {
    throw new Error(
      `Cannot parse Excel workbook: ${error.message}`
    );
  }

  const worksheet = workbook.worksheets[0];

  if (!worksheet) {
    return {
      headers: [],
      data: [],
    };
  }

  const usedHeaders = new Set();
  const maxColumn = worksheet.columnCount;
  const headerRow = worksheet.getRow(1);

  const headers = Array.from(
    { length: maxColumn },
    (_, index) => {
      const text = headerRow
        .getCell(index + 1)
        .text
        .trim();

      const base =
        text || `Column ${index + 1}`;

      let name = base;
      let suffix = 2;

      while (usedHeaders.has(name)) {
        name = `${base} (${suffix})`;
        suffix++;
      }

      usedHeaders.add(name);
      return name;
    }
  );

  const data = [];

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const record = {};
    let hasValues = false;

    headers.forEach((header, index) => {
      const cell = row.getCell(index + 1);

      const value = cell.text ?? "";

      record[header] = value;

      if (String(value).trim()) {
        hasValues = true;
      }
    });

    if (hasValues) {
      data.push(record);
    }
  });

  return {
    headers,
    data,
  };
}

// ------------------------------------------
// Download Excel file
// ------------------------------------------

export async function downloadInventoryExcel(
  filters = {}
) {
  const { blob, contentDisposition } =
    await getExcelBlob(filters);

  let filename = "inventory_export.xlsx";

  const match = contentDisposition.match(
    /filename="?([^";]+)"?/i
  );

  if (match?.[1]) {
    filename = match[1];
  }

  // Avoid using any directory supplied
  // by the server as a download filename.
  filename = filename
    .split(/[\\/]/)
    .pop()
    .replace(/[<>:"|?*]/g, "_");

  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
}
